/**
 * ProfileCommunity.jsx — Phase 1 unified Profile Interaction & Memorial
 * Community panel that lives inside every /family/:tree/member/:id page.
 *
 * The component fetches `/api/persons/:id/community` and adapts:
 *   • "living" mode  → Comments / News / Suggestion / Contact / …
 *   • "memorial" mode → Condolences / Missing You / Memory / Eulogy / …
 *
 * Reactions on posts also change per mode:
 *   • living  → 👍 ❤️ 👏 💡 🤗
 *   • memorial → 🕯 🌹 🙏 ❤️ 🕊
 *
 * Only signed-in users can post / react. Anonymous visitors see the
 * public feed and are nudged to sign in when they interact.
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, Send, Flag, MessageCircle, ChevronDown, ChevronUp } from "lucide-react";
import TranslatableText from "@/components/TranslatableText";

const TAB_ALL = { key: "all", label: "All", count: null };

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    const diffH = (Date.now() - dt.getTime()) / 3.6e6;
    if (diffH < 24) return `${Math.max(1, Math.floor(diffH))}h ago`;
    if (diffH < 24 * 7) return `${Math.floor(diffH / 24)}d ago`;
    return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
}

function locationLine(loc) {
  if (!loc) return "";
  return [loc.city_village, loc.district_county, loc.state_province, loc.country].filter(Boolean).join(", ");
}

export default function ProfileCommunity({ personId }) {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);

  const isMemorial = config?.mode === "memorial";

  const loadConfig = async () => {
    try {
      const { data } = await http.get(`/persons/${personId}/community`);
      setConfig(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load community");
    }
  };

  const loadPosts = async (cat = activeTab) => {
    setLoading(true);
    try {
      const params = {};
      if (cat && cat !== "all") params.category = cat;
      const { data } = await http.get(`/persons/${personId}/posts`, { params });
      setPosts(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load posts");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadConfig(); /* eslint-disable-next-line */ }, [personId]);
  useEffect(() => { loadPosts(activeTab); /* eslint-disable-next-line */ }, [personId, activeTab]);

  if (!config) return null;

  const tabs = [TAB_ALL, ...config.categories];

  const onPosted = () => {
    setComposerOpen(false);
    loadPosts(activeTab);
    loadConfig();
  };

  return (
    <section className="wofy-card" id="community" data-testid="profile-community">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="overline mb-1" style={{ color: isMemorial ? "#5B4A73" : "var(--wofy-primary)" }}>
            {isMemorial ? "MEMORIES & MEMORIAL" : "COMMENTS & UPDATES"}
          </div>
          <h2 className="font-display text-2xl">
            {isMemorial ? "Share a memory, tribute, or condolence" : "Share a memory, update, or reconnect"}
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            {isMemorial
              ? "Every message stays attached to this person's family profile — a permanent digital record for future generations."
              : "Everything stays linked to this person's canonical profile. Nothing forks off into a separate forum."}
          </p>
        </div>
        <button
          onClick={() => setComposerOpen((v) => !v)}
          className="wofy-btn-primary text-sm"
          data-testid="community-open-composer"
        >
          {composerOpen ? "Close" : (isMemorial ? "Share a Memory" : "Post an Update")}
        </button>
      </div>

      {config.mode === "memorial" && config.reactions?.some((r) => r.count > 0) && (
        <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b" style={{ borderColor: "var(--wofy-line)" }} data-testid="community-memorial-totals">
          {config.reactions.map((r) => (
            <span key={r.key} className="text-sm flex items-center gap-1.5" style={{ color: "var(--wofy-text-soft)" }}>
              <span className="text-lg">{r.emoji}</span>
              <b>{r.count}</b>
              <span className="capitalize text-xs opacity-70">{r.key.replace("_", " ")}</span>
            </span>
          ))}
        </div>
      )}

      {composerOpen && (
        <Composer
          personId={personId}
          categories={config.categories}
          mode={config.mode}
          signedIn={!!user}
          onPosted={onPosted}
        />
      )}

      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto mt-2 pb-3 border-b" style={{ borderColor: "var(--wofy-line)" }} data-testid="community-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            data-testid={`community-tab-${t.key}`}
            className="whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider transition-colors"
            style={{
              background: activeTab === t.key ? "var(--wofy-primary)" : "transparent",
              color: activeTab === t.key ? "#fff" : "var(--wofy-text-soft)",
              border: `1px solid ${activeTab === t.key ? "transparent" : "var(--wofy-line)"}`,
            }}
          >
            {t.label}{t.count ? ` · ${t.count}` : ""}
          </button>
        ))}
      </div>

      {/* Feed */}
      <div className="mt-4">
        {loading ? (
          <div className="py-10 text-center opacity-60"><Loader2 className="animate-spin inline w-4 h-4 mr-2" /> Loading…</div>
        ) : posts.length === 0 ? (
          <div className="py-10 text-center opacity-60 text-sm" data-testid="community-empty">
            {isMemorial ? "No memories shared yet. Be the first to honour their memory." : "No posts yet — share the first update."}
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((p) => (
              <PostCard
                key={p.id}
                post={p}
                reactionSet={config.reactions}
                signedIn={!!user}
                onChange={() => loadPosts(activeTab)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- Composer ----------
function Composer({ personId, categories, mode, signedIn, onPosted }) {
  const [category, setCategory] = useState(categories[0]?.key);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [relationship, setRelationship] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!signedIn) {
    return (
      <div className="rounded-xl border p-4 my-4 text-sm" style={{ borderColor: "var(--wofy-line)", background: "var(--wofy-surface)" }}>
        <b>Please sign in</b> to share a memory or comment. Signing in lets your family reply to you and lets you edit your own contributions.
      </div>
    );
  }

  const submit = async () => {
    if (!body.trim() || !category) return;
    setSubmitting(true);
    try {
      await http.post(`/persons/${personId}/posts`, {
        category,
        title: title.trim() || null,
        body: body.trim(),
        author_relationship: relationship.trim() || null,
        author_location: (city || country) ? { city_village: city.trim() || null, country: country.trim() || null } : null,
        visibility: "public",
      });
      toast.success(mode === "memorial" ? "Memory shared" : "Post added");
      setTitle(""); setBody(""); setRelationship("");
      onPosted?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not post");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-xl border p-4 my-4 space-y-3" style={{ borderColor: "var(--wofy-line)", background: "var(--wofy-surface)" }} data-testid="community-composer">
      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c.key}
            onClick={() => setCategory(c.key)}
            data-testid={`composer-cat-${c.key}`}
            className="text-xs px-2.5 py-1 rounded-full border transition-colors"
            style={{
              borderColor: category === c.key ? "var(--wofy-primary)" : "var(--wofy-line)",
              background: category === c.key ? "var(--wofy-primary)" : "transparent",
              color: category === c.key ? "#fff" : "var(--wofy-text)",
            }}
          >
            {c.label}
          </button>
        ))}
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (optional)"
        maxLength={180}
        className="w-full rounded-lg border px-3 py-2 text-sm"
        style={{ borderColor: "var(--wofy-line)" }}
        data-testid="composer-title"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={mode === "memorial"
          ? "Share a memory, condolence, prayer or tribute…"
          : "Write a comment, share an update or suggestion…"}
        rows={4}
        maxLength={8000}
        className="w-full rounded-lg border px-3 py-2 text-sm"
        style={{ borderColor: "var(--wofy-line)" }}
        data-testid="composer-body"
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input
          value={relationship}
          onChange={(e) => setRelationship(e.target.value)}
          placeholder="Your relationship (e.g. Cousin, Friend)"
          maxLength={60}
          className="rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "var(--wofy-line)" }}
          data-testid="composer-relationship"
        />
        <input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="City / Village"
          className="rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "var(--wofy-line)" }}
          data-testid="composer-city"
        />
        <input
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="Country"
          className="rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "var(--wofy-line)" }}
          data-testid="composer-country"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={submit}
          disabled={submitting || !body.trim() || !category}
          className="wofy-btn-primary text-sm"
          data-testid="composer-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {submitting ? "Sharing…" : (mode === "memorial" ? "Share Memory" : "Post")}
        </button>
      </div>
    </div>
  );
}

// ---------- PostCard ----------
function PostCard({ post, reactionSet, signedIn, onChange }) {
  const [showReplies, setShowReplies] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [myReactions, setMyReactions] = useState(post.my_reactions || []);
  const [reactions, setReactions] = useState(post.reactions || {});

  const react = async (key) => {
    if (!signedIn) return toast.error("Please sign in to react");
    try {
      const { data } = await http.post(`/posts/${post.id}/reactions`, { reaction: key });
      setReactions((r) => ({ ...r, [key]: Math.max(0, (r[key] || 0) + (data.state === "added" ? 1 : -1)) }));
      setMyReactions((m) => data.state === "added" ? [...m, key] : m.filter((k) => k !== key));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not react"); }
  };

  const openReplies = async () => {
    if (showReplies) return setShowReplies(false);
    setShowReplies(true);
    if (comments.length > 0) return;
    setLoadingComments(true);
    try {
      const { data } = await http.get(`/posts/${post.id}`);
      setComments(data.comments || []);
    } catch (e) { toast.error("Could not load replies"); }
    finally { setLoadingComments(false); }
  };

  const addComment = async () => {
    if (!signedIn) return toast.error("Please sign in to reply");
    if (!reply.trim()) return;
    setPosting(true);
    try {
      const { data } = await http.post(`/posts/${post.id}/comments`, { body: reply.trim() });
      setComments((c) => [...c, data]);
      setReply("");
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post reply"); }
    finally { setPosting(false); }
  };

  const report = async () => {
    if (!signedIn) return toast.error("Please sign in to report");
    const reason = window.prompt("Report reason (spam / harassment / private_information / incorrect_information / impersonation / copyright / inappropriate / other):", "spam");
    if (!reason) return;
    try {
      await http.post(`/posts/${post.id}/report`, { reason, notes: null });
      toast.success("Thanks — a moderator will review");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not report"); }
  };

  const loc = locationLine(post.author?.location);
  const badge = post.category_label || post.category;

  return (
    <article className="rounded-xl border p-4" style={{ borderColor: "var(--wofy-line)", background: "var(--wofy-paper)" }} data-testid={`community-post-${post.id}`}>
      <header className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-sm">
            <b>{post.author?.display_name || "Someone"}</b>
            {post.author?.relationship && <span className="opacity-70"> · {post.author.relationship}</span>}
          </div>
          {loc && <div className="text-xs opacity-60">{loc}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>
            {badge}
          </span>
          <span className="text-xs opacity-60">{formatWhen(post.created_at)}</span>
        </div>
      </header>
      {post.title && <h3 className="font-display text-lg mb-1">{post.title}</h3>}
      <TranslatableText
        text={post.body}
        className="text-sm"
        minChars={10}
        testid={`community-post-body-${post.id}`}
      />

      {/* Reaction row */}
      <footer className="mt-3 pt-3 border-t flex items-center flex-wrap gap-2" style={{ borderColor: "var(--wofy-line)" }}>
        {reactionSet.map((r) => {
          const active = myReactions.includes(r.key);
          const n = reactions[r.key] || 0;
          return (
            <button
              key={r.key}
              onClick={() => react(r.key)}
              data-testid={`community-react-${r.key}-${post.id}`}
              className="text-sm px-2 py-1 rounded-full border transition-colors"
              style={{
                borderColor: active ? "var(--wofy-primary)" : "var(--wofy-line)",
                background: active ? "rgba(196,127,58,0.15)" : "transparent",
              }}
              title={r.key}
            >
              <span className="text-base">{r.emoji}</span>
              {n > 0 && <span className="ml-1 text-xs opacity-70">{n}</span>}
            </button>
          );
        })}
        <button onClick={openReplies} className="ml-auto text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1" data-testid={`community-toggle-comments-${post.id}`}>
          <MessageCircle className="w-3.5 h-3.5" />
          {post.comments_count || 0} {post.comments_count === 1 ? "reply" : "replies"}
          {showReplies ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <button onClick={report} className="text-xs opacity-50 hover:opacity-100 inline-flex items-center gap-1" data-testid={`community-report-${post.id}`}>
          <Flag className="w-3.5 h-3.5" />
        </button>
      </footer>

      {showReplies && (
        <div className="mt-3 pl-3 border-l-2 space-y-2" style={{ borderColor: "var(--wofy-line)" }}>
          {loadingComments ? (
            <div className="text-xs opacity-60"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading replies…</div>
          ) : comments.length === 0 ? (
            <div className="text-xs opacity-60">No replies yet.</div>
          ) : comments.map((c) => (
            <div key={c.id} className="text-sm" data-testid={`community-comment-${c.id}`}>
              <b className="text-xs">{c.author_display_name}</b>
              <span className="text-[11px] opacity-60 ml-2">{formatWhen(c.created_at)}</span>
              <TranslatableText text={c.body} className="text-sm" minChars={10} testid={`community-comment-body-${c.id}`} />
            </div>
          ))}
          {signedIn ? (
            <div className="flex gap-2 pt-2">
              <input
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                placeholder="Write a reply…"
                className="flex-1 rounded-lg border px-3 py-1.5 text-sm"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid={`community-reply-input-${post.id}`}
              />
              <button
                onClick={addComment}
                disabled={posting || !reply.trim()}
                className="wofy-btn-primary text-xs"
                data-testid={`community-reply-submit-${post.id}`}
              >
                {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Reply"}
              </button>
            </div>
          ) : (
            <div className="text-xs opacity-60 pt-2">Sign in to reply.</div>
          )}
        </div>
      )}
    </article>
  );
}
